import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { proxySettingApi, F905 } from '../../../services/proxySettingApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useProxySettingForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F905[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [empCode, setEmpCode] = useState('');
  const [proxy1, setProxy1] = useState('');
  const [proxy2, setProxy2] = useState('');
  const [proxy3, setProxy3] = useState('');
  const [proxy4, setProxy4] = useState('');
  const [proxy5, setProxy5] = useState('');
  const [dept1, setDept1] = useState('');
  const [dept2, setDept2] = useState('');
  const [dept3, setDept3] = useState('');
  const [dept4, setDept4] = useState('');
  const [dept5, setDept5] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await proxySettingApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  const syncFromRecord = useCallback((rec: F905 | null) => {
    if (rec) {
      setEmpCode(rec.f905_01);
      setProxy1(rec.f905_11);
      setProxy2(rec.f905_12);
      setProxy3(rec.f905_13);
      setProxy4(rec.f905_14);
      setProxy5(rec.f905_15);
      setDept1(rec.f905_21);
      setDept2(rec.f905_22);
      setDept3(rec.f905_23);
      setDept4(rec.f905_24);
      setDept5(rec.f905_25);
    }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      syncFromRecord(current);
    }
  }, [current, mode, syncFromRecord]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setEmpCode('');
    setProxy1(''); setProxy2(''); setProxy3(''); setProxy4(''); setProxy5('');
    setDept1(''); setDept2(''); setDept3(''); setDept4(''); setDept5('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFromRecord(current);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      syncFromRecord(current);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await proxySettingApi.create({
          f905_01: empCode,
          f905_11: proxy1, f905_12: proxy2, f905_13: proxy3,
          f905_14: proxy4, f905_15: proxy5,
          f905_21: dept1, f905_22: dept2, f905_23: dept3,
          f905_24: dept4, f905_25: dept5,
        });
        message.success('新增成功');
      } else {
        await proxySettingApi.update(current!.f905_01, {
          f905_11: proxy1, f905_12: proxy2, f905_13: proxy3,
          f905_14: proxy4, f905_15: proxy5,
          f905_21: dept1, f905_22: dept2, f905_23: dept3,
          f905_24: dept4, f905_25: dept5,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? empCode : current!.f905_01;
      const idx = data.findIndex(r => r.f905_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await proxySettingApi.delete(current.f905_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f905_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    empCode, setEmpCode,
    proxy1, setProxy1, proxy2, setProxy2,
    proxy3, setProxy3, proxy4, setProxy4,
    proxy5, setProxy5,
    dept1, setDept1, dept2, setDept2,
    dept3, setDept3, dept4, setDept4,
    dept5, setDept5,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
